import { reactive, readonly } from 'vue';
import ContactAPI from 'dashboard/api/contacts';

// Counts for the pills of the contacts header ("Todos 6,4k", "Clientes 62").
// The account total comes from the plain index endpoint and each saved
// segment from one `filter` request with its stored query, page 1 — the
// same calls the list itself makes, so no new backend. Results are cached
// at module level for a couple of minutes and shared by every header.
const CACHE_TTL_MS = 2 * 60 * 1000;

const state = reactive({
  total: null,
  segments: {},
  fetchedAt: 0,
  segmentKey: '',
});

let inFlight = null;

const segmentKeyOf = segments =>
  (segments || []).map(segment => `${segment.id}:${segment.updatedAt || ''}`).join('|');

const fetchTotal = async () => {
  const { data } = await ContactAPI.get(1, 'name');
  return Number(data?.meta?.count ?? 0);
};

const fetchSegmentCount = async segment => {
  if (!segment?.query) return null;
  const { data } = await ContactAPI.filter(1, 'name', segment.query);
  return Number(data?.meta?.count ?? 0);
};

const refresh = async (segments, { force = false } = {}) => {
  const key = segmentKeyOf(segments);
  const fresh = Date.now() - state.fetchedAt < CACHE_TTL_MS;
  if (!force && fresh && key === state.segmentKey) return;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const [total, ...counts] = await Promise.all([
      fetchTotal().catch(() => null),
      ...(segments || []).map(segment =>
        fetchSegmentCount(segment).catch(() => null)
      ),
    ]);
    if (total !== null) state.total = total;
    (segments || []).forEach((segment, index) => {
      if (counts[index] !== null) state.segments[segment.id] = counts[index];
    });
    state.fetchedAt = Date.now();
    state.segmentKey = key;
  })().finally(() => {
    inFlight = null;
  });

  return inFlight;
};

// "6,4k" like the mockup for anything past 999, plain digits otherwise.
export const compactCount = value => {
  if (value === null || value === undefined) return '';
  const count = Number(value);
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1).replace(/\.0$/, '').replace('.', ',')}k`;
  }
  return String(count);
};

export const useContactCounts = () => ({
  counts: readonly(state),
  refresh,
});
